/** Account and owned-draft contracts; no browser, database or authentication I/O. */
import { FINGERPRINT, isRefusal, parseProject, type Project } from "./project";

export const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const ROLES = ["user", "admin"] as const;
export const STATUSES = ["pending", "approved", "rejected", "suspended"] as const;
export type Role = (typeof ROLES)[number];
export type AccessStatus = (typeof STATUSES)[number];
export const DEFAULT_ALLOWANCE_MS = 3_600_000;
export const MAX_ALLOWANCE_MS = 365 * 24 * 3_600_000;

export interface Balance {
  readonly period: string;
  readonly monthlyAllowanceMs: number | null;
  readonly allowanceMs: number | null;
  readonly usedMs: number;
  readonly heldMs: number;
  readonly remainingMs: number | null;
}
export interface Viewer {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly emailVerified: boolean;
  readonly role: Role;
  readonly status: AccessStatus;
  readonly balance: Balance;
}
export interface DraftSummary {
  readonly id: string;
  readonly title: string;
  readonly fingerprint: string;
  readonly name: string;
  readonly size: number;
  readonly words: number;
  readonly revision: number;
  readonly updatedAt: string;
}
export interface DraftRecord extends DraftSummary {
  readonly document: Project;
}

export function object(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : null;
}
export function nonnegative(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
export function allowance(value: unknown): value is number | null {
  return value === null || (nonnegative(value) && value <= MAX_ALLOWANCE_MS);
}
export function title(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0 && value.length <= 255;
}
export function parseSummary(value: unknown): DraftSummary | null {
  const row = object(value);
  if (row === null || typeof row.id !== "string" || !UUID.test(row.id) || !title(row.title)
    || typeof row.fingerprint !== "string" || !FINGERPRINT.test(row.fingerprint)
    || typeof row.name !== "string" || !nonnegative(row.size) || !nonnegative(row.words)
    || !nonnegative(row.revision) || row.revision < 1 || typeof row.updatedAt !== "string") return null;
  return { id: row.id, title: row.title, fingerprint: row.fingerprint, name: row.name, size: row.size,
    words: row.words, revision: row.revision, updatedAt: row.updatedAt };
}
export function parseDraft(value: unknown): DraftRecord | null {
  const summary = parseSummary(value);
  const row = object(value);
  if (summary === null || row === null) return null;
  const document = parseProject(row.document);
  return isRefusal(document) ? null : { ...summary, document };
}
export function parseViewer(value: unknown): Viewer | null {
  const row = object(value);
  const balance = object(row?.balance);
  if (row === null || balance === null || typeof row.id !== "string" || typeof row.email !== "string"
    || typeof row.name !== "string" || typeof row.emailVerified !== "boolean"
    || !ROLES.some((role) => role === row.role) || !STATUSES.some((status) => status === row.status)
    || typeof balance.period !== "string" || !/^\d{4}-\d{2}$/.test(balance.period)
    || !allowance(balance.monthlyAllowanceMs) || !allowance(balance.allowanceMs)
    || !nonnegative(balance.usedMs) || !nonnegative(balance.heldMs)
    || !(balance.remainingMs === null || nonnegative(balance.remainingMs))) return null;
  // The two enum assertions follow exact membership checks above.
  return { id: row.id, email: row.email, name: row.name, emailVerified: row.emailVerified,
    role: row.role as Role, status: row.status as AccessStatus,
    balance: { period: balance.period, monthlyAllowanceMs: balance.monthlyAllowanceMs,
      allowanceMs: balance.allowanceMs, usedMs: balance.usedMs, heldMs: balance.heldMs,
      remainingMs: balance.remainingMs } };
}
